import { useMemo } from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts"
import { Box, Flex, Text, Badge } from "@chakra-ui/react"
import { Chart, useChart } from "@chakra-ui/charts"
import {
  appendTimeMs,
  timescaleChartMarginRight,
  timescaleTickFormatter,
  niceYAxisMax,
  type ChartHighlightRange,
  chartHighlightToMsRange,
} from "./TimeSeries"
import { PEAK_COLOR, PHASE_COLORS } from "./graphColors"
import PerAssetPhaseChart from "./PerAssetPhaseChart"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Raw per-consumer per-phase power data point.
 *
 * Mirrors the `PhaseDataPoint` defined in `useSystemMonitoringGraphs` but
 * declared locally so that `shared/react` does not depend on `systems`.
 */
export interface PhaseDataPoint {
  timestamp: string
  /** Consumer asset ID (or assetId_connection for connection-based) */
  consumerId: string
  /** Display name of the asset, when the API provides it */
  assetName?: string | null
  phase1: number | null
  phase2: number | null
  phase3: number | null
  phase1Peak: number | null
  phase2Peak: number | null
  phase3Peak: number | null
}

/**
 * Display mode for the phase balance chart.
 * - `"percentage"` – 100 % stacked bars (original behaviour).
 * - `"absolute"`  – standard stacked bars showing absolute kW values so that
 *   sudden increases on a single phase are clearly visible.
 */
export type PhaseChartMode = "percentage" | "absolute"

export interface PhaseBalanceChartProps {
  /** Per-consumer per-phase raw power data (Watts, pre-scaling) */
  phaseData: PhaseDataPoint[]
  /** Set of currently selected consumer asset IDs from the consumer filter */
  selectedConsumers: Set<string>
  /** Same time domain as the Load graph */
  timeDomain: [number, number]
  /** Shared x-axis tick positions (UTC ms) */
  xAxisTicksMs?: number[]
  /** Whether to scale W → kW */
  scaleToKw: boolean
  /** Callback when user hovers a series (for cross-chart highlight) */
  onSeriesHover?: (seriesName: string | null) => void
  /** Currently hovered series name */
  hoveredSeries?: string | null
  /** Shared timestamps for x-axis alignment */
  sharedTimestamps?: string[]
  /** Recharts syncId for synchronising tooltips across charts */
  syncId?: string
  /** Map from consumerId → display label for per-asset phase breakdown in tooltip */
  consumerLabelMap?: Map<string, string>
  /** Map from consumerId → colour string for per-asset phase breakdown in tooltip */
  consumerColorMap?: Map<string, string>
  /**
   * Load bar data keyed by timestamp → { seriesName → value }.
   * Used to compute the fraction of total load each connection represents
   * so phase values can be scaled proportionally when connections are toggled.
   */
  loadBarData?: Map<string, Map<string, number>>
  /** All load bar series names (including _remainder) */
  allLoadSeriesNames?: string[]
  /**
   * Chart display mode.
   * `"absolute"` (default) renders a standard stacked bar chart with the same
   * kW scale as the site-load chart above, making phase-level spikes obvious.
   * `"percentage"` renders the legacy 100 % stacked view.
   */
  mode?: PhaseChartMode
  /** Chart height override – used in absolute mode to match the site-load chart */
  chartHeight?: string
  /** Whether to show peak caps above the phase bars (controlled by the parent's Show Peaks toggle) */
  showPeaks?: boolean
  /** Time ranges to highlight on the chart (e.g. alert event periods) */
  highlightRanges?: ChartHighlightRange[]
  /** When set (e.g. 1, 2, or 3), the specified phase is visually emphasised and others are dimmed */
  highlightPhase?: 1 | 2 | 3
  /** Draw each ticked asset separately (side by side) instead of one combined stack. */
  perAsset?: boolean
}

// Phase colours

const PHASE_1_COLOR = PHASE_COLORS.l1 // red
const PHASE_2_COLOR = PHASE_COLORS.l2 // yellow
const PHASE_3_COLOR = PHASE_COLORS.l3 // blue

const PEAK_OPACITY = 0.6
const MIN_BAR_HEIGHT_PX = 3

// ---------------------------------------------------------------------------
// Peak cap shape for the topmost phase bar (Phase 3)
// ---------------------------------------------------------------------------

interface PhasePeakBarShapeProps {
  fill: string
  x: number
  y: number
  width: number
  height: number
  payload?: any
  dataKey?: string
  showPeaks?: boolean
  phase1DataKey: string
  phase2DataKey: string
  phase3DataKey: string
}

const PhasePeakBarShape = (props: PhasePeakBarShapeProps) => {
  const { fill, x, y, width, height, payload, showPeaks, phase1DataKey, phase2DataKey, phase3DataKey } = props

  const baseBar = (
    <rect x={x} y={y} width={width} height={Math.max(0, height)} fill={fill} />
  )

  if (!showPeaks || !payload) return baseBar

  // Sum all phase averages and all phase peaks
  const p1 = typeof payload[phase1DataKey] === "number" ? payload[phase1DataKey] : 0
  const p2 = typeof payload[phase2DataKey] === "number" ? payload[phase2DataKey] : 0
  const p3 = typeof payload[phase3DataKey] === "number" ? payload[phase3DataKey] : 0
  const totalAvg = p1 + p2 + p3

  const pk1 = typeof payload.phase1Peak === "number" ? payload.phase1Peak : 0
  const pk2 = typeof payload.phase2Peak === "number" ? payload.phase2Peak : 0
  const pk3 = typeof payload.phase3Peak === "number" ? payload.phase3Peak : 0
  const totalPeak = pk1 + pk2 + pk3

  if (totalPeak <= totalAvg || totalAvg <= 0 || height <= 0) return baseBar

  const peakExtension = totalPeak - totalAvg
  // The topmost bar's pixel height maps to its value; use ratio to estimate peak px
  const currentBarValue = p3 > 0 ? p3 : 1
  const estimatedPeakPx = (peakExtension / currentBarValue) * height
  const finalPeakHeight = Math.max(estimatedPeakPx, MIN_BAR_HEIGHT_PX)
  const peakY = y - finalPeakHeight

  return (
    <g>
      {baseBar}
      <rect
        x={x}
        y={peakY}
        width={width}
        height={finalPeakHeight}
        fill={PEAK_COLOR}
        opacity={PEAK_OPACITY}
      />
    </g>
  )
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Compute the percentage split across three phase power values.
 *
 * Negative values are treated as zero. When the total is zero all percentages
 * are returned as 0.
 */
export function computePhasePercentages(
  phase1: number,
  phase2: number,
  phase3: number,
): { phase1Pct: number; phase2Pct: number; phase3Pct: number } {
  const p1 = Math.max(0, phase1)
  const p2 = Math.max(0, phase2)
  const p3 = Math.max(0, phase3)
  const total = p1 + p2 + p3
  if (total === 0) {
    return { phase1Pct: 0, phase2Pct: 0, phase3Pct: 0 }
  }
  return {
    phase1Pct: (p1 / total) * 100,
    phase2Pct: (p2 / total) * 100,
    phase3Pct: (p3 / total) * 100,
  }
}

/**
 * Returns `true` when at least one data point carries a non-null phase value.
 */
export function hasPhaseData(phaseData: PhaseDataPoint[]): boolean {
  return phaseData.some(
    (p) => p.phase1 !== null || p.phase2 !== null || p.phase3 !== null,
  )
}

// ---------------------------------------------------------------------------
// Aggregated data point used internally for charting
// ---------------------------------------------------------------------------

interface AggregatedPhasePoint {
  timestamp: string
  phase1Total: number
  phase2Total: number
  phase3Total: number
  phase1Pct: number
  phase2Pct: number
  phase3Pct: number
  /** Absolute values in display unit (kW or W) for absolute-mode bars */
  phase1Abs: number
  phase2Abs: number
  phase3Abs: number
  /** Peak values in display unit (kW or W) */
  phase1Peak: number
  phase2Peak: number
  phase3Peak: number
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

interface PhaseBalanceTooltipProps {
  active?: boolean
  payload?: any[]
  label?: string
  phaseAggregatedData: Map<string, { phase1: number; phase2: number; phase3: number }>
  phasePeakData?: Map<string, { phase1Peak: number; phase2Peak: number; phase3Peak: number }>
  unit: string
  /** Per-consumer per-phase data keyed by timestamp → consumerId → phase values */
  perConsumerData?: Map<string, Map<string, { phase1: number; phase2: number; phase3: number }>>
  /** Map from consumerId → display label */
  consumerLabelMap?: Map<string, string>
  /** Map from consumerId → colour */
  consumerColorMap?: Map<string, string>
  /** Whether to show per-consumer breakdown */
  showBreakdown?: boolean
  /** Whether to show peak values */
  showPeaks?: boolean
}

const PhaseBalanceTooltip = ({
  active,
  payload,
  label,
  phaseAggregatedData,
  phasePeakData,
  unit,
  perConsumerData,
  consumerLabelMap,
  consumerColorMap,
  showBreakdown,
  showPeaks,
}: PhaseBalanceTooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  // Resolve the timestamp from the numeric timeMs label
  const labelMs = typeof label === "number" ? label : Number(label)
  const date = new Date(Number.isFinite(labelMs) ? labelMs : String(label || ""))
  const formattedDate = date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

  const dataPoint = payload[0]?.payload as (AggregatedPhasePoint & { timeMs: number }) | undefined
  const ts = dataPoint?.timestamp
  const raw = ts ? phaseAggregatedData.get(ts) : undefined
  const peaks = ts && phasePeakData ? phasePeakData.get(ts) : undefined

  // Order top-to-bottom matching the stack (Phase 3 is topmost bar)
  const phases = [
    { key: "phase3", peakKey: "phase3Peak" as const, name: "Phase 3", color: PHASE_3_COLOR, pct: dataPoint?.phase3Pct ?? 0, value: raw?.phase3 ?? 0, peak: peaks?.phase3Peak ?? 0 },
    { key: "phase2", peakKey: "phase2Peak" as const, name: "Phase 2", color: PHASE_2_COLOR, pct: dataPoint?.phase2Pct ?? 0, value: raw?.phase2 ?? 0, peak: peaks?.phase2Peak ?? 0 },
    { key: "phase1", peakKey: "phase1Peak" as const, name: "Phase 1", color: PHASE_1_COLOR, pct: dataPoint?.phase1Pct ?? 0, value: raw?.phase1 ?? 0, peak: peaks?.phase1Peak ?? 0 },
  ]

  const consumerBreakdown = ts && perConsumerData ? perConsumerData.get(ts) : undefined
  const hasConsumerBreakdown = showBreakdown && consumerBreakdown && consumerBreakdown.size > 0

  const formatValue = (v: number) =>
    v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })

  return (
    <Box
      display="inline-block"
      position="relative"
      left="calc(-100% - 0px)"
    >
      <Box
        backgroundColor="card"
        borderWidth="1px"
        borderColor="border"
        borderRadius="md"
        padding={3}
        boxShadow="lg"
        minW="220px"
        maxW="400px"
        zIndex="tooltip"
      >
      <Text fontSize="sm" fontWeight="bold" color="primary" mb={2}>
        {formattedDate}
      </Text>
      <Flex direction="column" gap={1}>
        {phases.map((phase) => {
          const consumers = hasConsumerBreakdown
            ? Array.from(consumerBreakdown.entries())
                .map(([consumerId, values]) => ({
                  consumerId,
                  label: consumerLabelMap?.get(consumerId) ?? consumerId,
                  value: values[phase.key as keyof typeof values],
                }))
                .filter((c) => c.value > 0)
                .sort((a, b) => b.value - a.value)
            : []

          return (
            <Box key={phase.key}>
              <Flex alignItems="center" gap={2}>
                <Badge
                  backgroundColor={phase.color}
                  color="white"
                  fontSize="xs"
                  minW="12px"
                  textAlign="center"
                >
                  ●
                </Badge>
                <Text fontSize="sm" color="primary" flex="1">
                  {phase.name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {phase.pct.toFixed(1)}%
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color="primary">
                  {formatValue(phase.value)} {unit}
                </Text>
              </Flex>

              {showPeaks && phase.peak > 0 && phase.peak > phase.value && (
                <Flex alignItems="center" gap={2} ml="22px">
                  <Badge
                    backgroundColor={PEAK_COLOR}
                    color="white"
                    fontSize="xs"
                    minW="12px"
                    textAlign="center"
                  >
                    ▲
                  </Badge>
                  <Text fontSize="xs" color="fg.muted" flex="1">
                    Peak
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold" color="primary">
                    {formatValue(phase.peak)} {unit}
                  </Text>
                </Flex>
              )}

              {consumers.length > 0 && (
                <Flex direction="column" gap={0.5} ml="22px" mt={0.5} mb={1}>
                  {consumers.map((consumer) => (
                    <Flex key={consumer.consumerId} alignItems="center" gap={2}>
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="sm"
                        bg={consumerColorMap?.get(consumer.consumerId) ?? "gray.400"}
                        flexShrink={0}
                      />
                      <Text fontSize="xs" color="fg.muted" flex="1" truncate>
                        {consumer.label}
                      </Text>
                      <Text fontSize="xs" fontWeight="medium" color="primary">
                        {formatValue(consumer.value)} {unit}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Box>
          )
        })}
      </Flex>
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PHASE_CHART_HEIGHT_PERCENTAGE = 120
const PHASE_CHART_HEIGHT_ABSOLUTE = "400px"

const PhaseBalanceChartAggregated = ({
  phaseData,
  selectedConsumers,
  timeDomain,
  xAxisTicksMs,
  scaleToKw,
  onSeriesHover: _onSeriesHover,
  hoveredSeries: _hoveredSeries,
  sharedTimestamps,
  syncId,
  consumerLabelMap,
  consumerColorMap,
  loadBarData,
  allLoadSeriesNames,
  mode = "absolute",
  chartHeight,
  showPeaks = false,
  highlightRanges,
  highlightPhase,
}: PhaseBalanceChartProps) => {
  const isAbsolute = mode === "absolute"
  const showBreakdown = false
  // 1. Aggregate per-phase values: start with total across ALL phase consumers,
  //    then scale down proportionally based on the fraction of load that is
  //    deselected in the consumer filter.
  //
  //    Phase data is at the asset level but the load graph may break down by
  //    connection. We use the load bar data to compute what fraction of total
  //    load is deselected, then reduce phase values by that fraction.
  const aggregatedData = useMemo((): AggregatedPhasePoint[] => {
    // Compute total phase values across ALL consumers per timestamp
    const totalByTimestamp = new Map<string, {
      phase1: number; phase2: number; phase3: number
      phase1Peak: number; phase2Peak: number; phase3Peak: number
    }>()

    for (const point of phaseData) {
      const existing = totalByTimestamp.get(point.timestamp) ?? {
        phase1: 0, phase2: 0, phase3: 0,
        phase1Peak: 0, phase2Peak: 0, phase3Peak: 0,
      }
      existing.phase1 += Math.max(0, point.phase1 ?? 0)
      existing.phase2 += Math.max(0, point.phase2 ?? 0)
      existing.phase3 += Math.max(0, point.phase3 ?? 0)
      // Peaks are summed across consumers (each consumer's peak contributes to the total)
      existing.phase1Peak += Math.max(0, point.phase1Peak ?? 0)
      existing.phase2Peak += Math.max(0, point.phase2Peak ?? 0)
      existing.phase3Peak += Math.max(0, point.phase3Peak ?? 0)
      totalByTimestamp.set(point.timestamp, existing)
    }

    // If sharedTimestamps is provided, use those for x-axis alignment
    const timestamps = sharedTimestamps ?? Array.from(totalByTimestamp.keys()).sort()

    // Check if all consumers are selected (no scaling needed)
    const allSelected = !allLoadSeriesNames || allLoadSeriesNames.every((name) => selectedConsumers.has(name))

    return timestamps.map((ts) => {
      const total = totalByTimestamp.get(ts) ?? {
        phase1: 0, phase2: 0, phase3: 0,
        phase1Peak: 0, phase2Peak: 0, phase3Peak: 0,
      }

      let phase1 = total.phase1
      let phase2 = total.phase2
      let phase3 = total.phase3
      let p1Peak = total.phase1Peak
      let p2Peak = total.phase2Peak
      let p3Peak = total.phase3Peak

      // Scale phase values based on the fraction of load that is selected
      if (!allSelected && loadBarData) {
        const loadRow = loadBarData.get(ts)
        if (loadRow) {
          let totalLoad = 0
          let selectedLoad = 0
          for (const [seriesName, value] of loadRow.entries()) {
            if (typeof value === "number" && Number.isFinite(value) && value > 0) {
              totalLoad += value
              if (selectedConsumers.has(seriesName)) {
                selectedLoad += value
              }
            }
          }
          if (totalLoad > 0) {
            const fraction = selectedLoad / totalLoad
            phase1 = total.phase1 * fraction
            phase2 = total.phase2 * fraction
            phase3 = total.phase3 * fraction
            p1Peak = total.phase1Peak * fraction
            p2Peak = total.phase2Peak * fraction
            p3Peak = total.phase3Peak * fraction
          }
        }
      }

      phase1 = Math.max(0, phase1)
      phase2 = Math.max(0, phase2)
      phase3 = Math.max(0, phase3)
      const { phase1Pct, phase2Pct, phase3Pct } = computePhasePercentages(phase1, phase2, phase3)
      const scale = scaleToKw ? 1 / 1000 : 1
      return {
        timestamp: ts,
        phase1Total: phase1,
        phase2Total: phase2,
        phase3Total: phase3,
        phase1Pct,
        phase2Pct,
        phase3Pct,
        phase1Abs: phase1 * scale,
        phase2Abs: phase2 * scale,
        phase3Abs: phase3 * scale,
        phase1Peak: Math.max(0, p1Peak) * scale,
        phase2Peak: Math.max(0, p2Peak) * scale,
        phase3Peak: Math.max(0, p3Peak) * scale,
      }
    })
  }, [phaseData, selectedConsumers, sharedTimestamps, loadBarData, allLoadSeriesNames, scaleToKw])

  // 2. Build a lookup map for the tooltip (raw values keyed by timestamp)
  const phaseAggregatedLookup = useMemo(() => {
    const map = new Map<string, { phase1: number; phase2: number; phase3: number }>()
    for (const point of aggregatedData) {
      const values = {
        phase1: scaleToKw ? point.phase1Total / 1000 : point.phase1Total,
        phase2: scaleToKw ? point.phase2Total / 1000 : point.phase2Total,
        phase3: scaleToKw ? point.phase3Total / 1000 : point.phase3Total,
      }
      map.set(point.timestamp, values)
    }
    return map
  }, [aggregatedData, scaleToKw])

  // 2a. Peak lookup for the tooltip
  const phasePeakLookup = useMemo(() => {
    const map = new Map<string, { phase1Peak: number; phase2Peak: number; phase3Peak: number }>()
    for (const point of aggregatedData) {
      map.set(point.timestamp, {
        phase1Peak: point.phase1Peak,
        phase2Peak: point.phase2Peak,
        phase3Peak: point.phase3Peak,
      })
    }
    return map
  }, [aggregatedData])

  // 2b. Per-consumer per-phase lookup for the tooltip breakdown
  // Only computed when showBreakdown is true to avoid unnecessary work
  const perConsumerLookup = useMemo(() => {
    if (!showBreakdown) return undefined
    const map = new Map<string, Map<string, { phase1: number; phase2: number; phase3: number }>>()
    for (const point of phaseData) {
      if (!selectedConsumers.has(point.consumerId)) continue
      if (!map.has(point.timestamp)) {
        map.set(point.timestamp, new Map())
      }
      const consumers = map.get(point.timestamp)!
      const scale = scaleToKw ? 1 / 1000 : 1
      consumers.set(point.consumerId, {
        phase1: Math.max(0, (point.phase1 ?? 0)) * scale,
        phase2: Math.max(0, (point.phase2 ?? 0)) * scale,
        phase3: Math.max(0, (point.phase3 ?? 0)) * scale,
      })
    }
    return map
  }, [showBreakdown, phaseData, selectedConsumers, scaleToKw])

  // 3. Append timeMs for Recharts numeric x-axis
  const dataForChart = useMemo(
    () => appendTimeMs(aggregatedData),
    [aggregatedData],
  )

  const unit = scaleToKw ? "kW" : "W"

  const phaseLegendItems = [
    { name: "Phase 3", color: PHASE_3_COLOR },
    { name: "Phase 2", color: PHASE_2_COLOR },
    { name: "Phase 1", color: PHASE_1_COLOR },
  ]

  const resolvedChartHeight = chartHeight
    ? chartHeight
    : isAbsolute
      ? PHASE_CHART_HEIGHT_ABSOLUTE
      : PHASE_CHART_HEIGHT_PERCENTAGE

  /** Y-axis tick formatter for absolute mode */
  const formatAbsYAxisTick = (value: number) => {
    if (value === 0) return "0"
    if (Math.abs(value) >= 1_000_000) {
      return value.toLocaleString(undefined, {
        notation: "compact",
        maximumFractionDigits: 1,
      })
    }
    if (Number.isInteger(value)) {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    }
    if (Math.abs(value) < 1) {
      return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")
    }
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  /** X-axis tick formatter for absolute mode (mirrors the site-load chart) */
  const formatAbsTimeTick = useMemo(
    () => timescaleTickFormatter(Math.abs(timeDomain[1] - timeDomain[0])),
    [timeDomain]
  )

  // Data keys change based on mode
  const phase1DataKey = isAbsolute ? "phase1Abs" : "phase1Pct"
  const phase2DataKey = isAbsolute ? "phase2Abs" : "phase2Pct"
  const phase3DataKey = isAbsolute ? "phase3Abs" : "phase3Pct"

  const phaseSeries = useMemo(() => [
    { name: phase1DataKey, color: PHASE_1_COLOR, label: "Phase 1", strokeWidth: 0, connectNulls: false },
    { name: phase2DataKey, color: PHASE_2_COLOR, label: "Phase 2", strokeWidth: 0, connectNulls: false },
    { name: phase3DataKey, color: PHASE_3_COLOR, label: "Phase 3", strokeWidth: 0, connectNulls: false },
  ], [phase1DataKey, phase2DataKey, phase3DataKey])

  const chart = useChart({
    data: dataForChart,
    series: phaseSeries,
  } as unknown as Parameters<typeof useChart>[0])

  /** Compute Y-axis domain that accommodates peak values above the stacked bars */
  const phaseYAxisDomain = useMemo((): [number | string, number | string] | undefined => {
    if (!isAbsolute || !showPeaks) return undefined
    let maxPeakTotal = 0
    for (const point of dataForChart) {
      const p = point as AggregatedPhasePoint
      const pk1 = typeof p.phase1Peak === "number" ? p.phase1Peak : 0
      const pk2 = typeof p.phase2Peak === "number" ? p.phase2Peak : 0
      const pk3 = typeof p.phase3Peak === "number" ? p.phase3Peak : 0
      const totalPeak = pk1 + pk2 + pk3
      if (totalPeak > maxPeakTotal) maxPeakTotal = totalPeak
    }
    if (maxPeakTotal > 0) {
      return [0, niceYAxisMax(maxPeakTotal * 1.05)]
    }
    return undefined
  }, [isAbsolute, showPeaks, dataForChart])

  return (
    <Box mt={isAbsolute ? 0 : -2} position="relative" css={{ "& .recharts-wrapper": { overflow: "visible" }, "& .recharts-tooltip-wrapper": { overflow: "visible" } }}>
      {/* Phase legend (centered, above chart) */}
      <Flex
        position="relative"
        alignItems="center"
        justifyContent="center"
        mb={1}
      >
        <Flex direction="row" gap={4} alignItems="center" flexWrap="wrap">
          {phaseLegendItems.map((item) => (
            <Flex key={item.name} alignItems="center" gap={1} fontSize="sm">
              <Box w="12px" h="12px" bg={item.color} borderRadius="sm" />
              <Text color="primary">{item.name}</Text>
            </Flex>
          ))}
        </Flex>
      </Flex>

      <Chart.Root h={isAbsolute ? resolvedChartHeight : PHASE_CHART_HEIGHT_PERCENTAGE} maxH={isAbsolute ? resolvedChartHeight : PHASE_CHART_HEIGHT_PERCENTAGE} chart={chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dataForChart}
            syncId={syncId}
            margin={{
              top: 15,
              right: timescaleChartMarginRight(false),
              left: 20,
              bottom: isAbsolute ? 60 : 5,
            }}
            barCategoryGap="6%"
          >
            <CartesianGrid stroke={chart.color("border")} strokeDasharray="3 3" vertical={false} />
            {highlightRanges?.map((range, index) => {
              const { x1, x2 } = chartHighlightToMsRange(range)
              return (
                <ReferenceArea
                  key={`highlight-${index}`}
                  x1={x1}
                  x2={x2}
                  fill={range.color || "rgba(255, 165, 0, 0.2)"}
                  fillOpacity={0.3}
                  stroke={range.color || "rgba(255, 165, 0, 0.6)"}
                  strokeWidth={4}
                />
              )
            })}
            <XAxis
              dataKey="timeMs"
              type="number"
              scale="time"
              domain={timeDomain}
              tick={isAbsolute ? undefined : false}
              axisLine={false}
              stroke={chart.color("chart_axis")}
              ticks={xAxisTicksMs}
              tickLine={false}
              fontSize={12}
              tickFormatter={isAbsolute ? formatAbsTimeTick : undefined}
              minTickGap={isAbsolute ? 40 : undefined}
            />
            {isAbsolute ? (
              <YAxis
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                stroke={chart.color("chart_axis")}
                fontSize={12}
                tickFormatter={formatAbsYAxisTick}
                allowDecimals={false}
                domain={phaseYAxisDomain ?? [0, (dataMax: number) => niceYAxisMax(dataMax)]}
                label={{
                  value: `Site Load (${unit})`,
                  angle: -90,
                  position: "insideLeft",
                }}
              />
            ) : (
              <YAxis
                domain={[0, 100]}
                ticks={[0, 50, 100]}
                tickFormatter={(v: number) => `${v}`}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
                stroke={chart.color("chart_axis")}
                fontSize={12}
                label={{
                  value: "Phase (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
            )}
            <Tooltip
              animationDuration={0}
              allowEscapeViewBox={{ x: true, y: true }}
              wrapperStyle={{ zIndex: 1000, pointerEvents: "none", overflow: "visible" }}
              offset={-10}
              position={{ y: 0 }}
              cursor={{ fill: "rgba(0, 0, 0, 0.06)" }}
              content={
                <PhaseBalanceTooltip
                  phaseAggregatedData={phaseAggregatedLookup}
                  phasePeakData={phasePeakLookup}
                  unit={unit}
                  perConsumerData={perConsumerLookup}
                  consumerLabelMap={consumerLabelMap}
                  consumerColorMap={consumerColorMap}
                  showBreakdown={showBreakdown}
                  showPeaks={showPeaks}
                />
              }
            />
            <Bar
              dataKey={chart.key(phase1DataKey as never)}
              stackId="phase"
              fill={chart.color(PHASE_1_COLOR)}
              fillOpacity={highlightPhase && highlightPhase !== 1 ? 0.2 : 1}
              isAnimationActive={false}
              maxBarSize={80}
            />
            <Bar
              dataKey={chart.key(phase2DataKey as never)}
              stackId="phase"
              fill={chart.color(PHASE_2_COLOR)}
              fillOpacity={highlightPhase && highlightPhase !== 2 ? 0.2 : 1}
              isAnimationActive={false}
              maxBarSize={80}
            />
            <Bar
              dataKey={chart.key(phase3DataKey as never)}
              stackId="phase"
              fill={chart.color(PHASE_3_COLOR)}
              fillOpacity={highlightPhase && highlightPhase !== 3 ? 0.2 : 1}
              isAnimationActive={false}
              maxBarSize={80}
              shape={isAbsolute && showPeaks ? (shapeProps: any) => (
                <PhasePeakBarShape
                  {...shapeProps}
                  showPeaks={showPeaks}
                  phase1DataKey={phase1DataKey}
                  phase2DataKey={phase2DataKey}
                  phase3DataKey={phase3DataKey}
                />
              ) : undefined}
            />
          </BarChart>
        </ResponsiveContainer>
      </Chart.Root>
    </Box>
  )
}

/**
 * Default: the combined view (one stack per interval, scaled by the Load menu).
 * With `perAsset` (absolute mode only) each ticked asset is drawn on its own
 * instead, so nothing is summed.
 */
const PhaseBalanceChart = (props: PhaseBalanceChartProps) => {
  const { perAsset, mode = "absolute" } = props
  if (perAsset && mode === "absolute") {
    return (
      <PerAssetPhaseChart
        phaseData={props.phaseData}
        selectedConsumers={props.selectedConsumers}
        timeDomain={props.timeDomain}
        xAxisTicksMs={props.xAxisTicksMs}
        scaleToKw={props.scaleToKw}
        sharedTimestamps={props.sharedTimestamps}
        syncId={props.syncId}
        consumerLabelMap={props.consumerLabelMap}
        chartHeight={props.chartHeight}
        highlightRanges={props.highlightRanges}
        highlightPhase={props.highlightPhase}
      />
    )
  }
  return <PhaseBalanceChartAggregated {...props} />
}

export default PhaseBalanceChart
